/**
 * TEFAS API Client Error Classes
 * 
 * This module defines business-focused error classes for TEFAS operations.
 * All errors are designed to be user-friendly and actionable.
 */

/**
 * Base error class for all API-related errors
 */
export class ApiError extends Error {
  public readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.cause = cause;
    
    // Maintain proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * Base error class for all TEFAS-related errors
 */
export class TefasError extends ApiError {
  public readonly code: string;
  public readonly details?: unknown;

  constructor(message: string, code: string, details?: unknown) {
    super(message);
    this.name = 'TefasError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Error thrown when an invalid fund type is provided
 */
export class InvalidFundTypeError extends TefasError {
  constructor(fundType: string) {
    const supportedTypes = ['EMK', 'YAT', 'BYF', 'GYF', 'GSYF'];
    super(
      `Invalid fund type '${fundType}'. Supported types are: ${supportedTypes.join(', ')}`,
      'INVALID_FUND_TYPE',
      { providedType: fundType, supportedTypes }
    );
    this.name = 'InvalidFundTypeError';
  }
}

/**
 * Error thrown when an invalid date format is provided
 */
export class InvalidDateError extends TefasError {
  constructor(date: string, reason: string) {
    super(
      `Invalid date '${date}': ${reason}. Please use YYYY-MM-DD format (e.g., 2024-01-01)`,
      'INVALID_DATE',
      { providedDate: date, reason }
    );
    this.name = 'InvalidDateError';
  }
}

/**
 * Error thrown when a specific fund code is not found
 */
export class FundNotFoundError extends TefasError {
  constructor(fundCode: string) {
    super(
      `Fund '${fundCode}' not found. Please check the fund code and try again`,
      'FUND_NOT_FOUND',
      { fundCode }
    );
    this.name = 'FundNotFoundError';
  }
}

/**
 * Error thrown when date range is invalid
 */
export class DateRangeError extends TefasError {
  constructor(startDate: string, endDate: string, reason: string) {
    super(
      `Invalid date range from '${startDate}' to '${endDate}': ${reason}`,
      'INVALID_DATE_RANGE',
      { startDate, endDate, reason }
    );
    this.name = 'DateRangeError';
  }
}

/**
 * Error thrown when TEFAS API returns an error
 */
export class TefasApiError extends TefasError {
  constructor(message: string, details?: unknown) {
    super(
      `TEFAS API error: ${message}`,
      'TEFAS_API_ERROR',
      details
    );
    this.name = 'TefasApiError';
  }
}

/**
 * Error thrown when network connectivity issues occur
 */
export class NetworkError extends ApiError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = 'NetworkError';
  }
}

/**
 * Error thrown when response validation fails
 */
export class ResponseValidationError extends TefasError {
  constructor(message: string, details?: unknown) {
    super(
      `Response validation failed: ${message}`,
      'RESPONSE_VALIDATION_ERROR',
      details
    );
    this.name = 'ResponseValidationError';
  }
}

/**
 * Error thrown when configuration is invalid
 */
export class ConfigurationError extends TefasError {
  constructor(message: string, details?: unknown) {
    super(
      `Configuration error: ${message}`,
      'CONFIGURATION_ERROR',
      details
    );
    this.name = 'ConfigurationError';
  }
}

/**
 * Type guard to check if an error is a TefasError
 */
export function isTefasError(error: unknown): error is TefasError {
  return error instanceof TefasError;
}

/**
 * Type guard to check if an error is an InvalidFundTypeError
 */
export function isInvalidFundTypeError(error: unknown): error is InvalidFundTypeError {
  return error instanceof InvalidFundTypeError;
}

/**
 * Type guard to check if an error is an InvalidDateError
 */
export function isInvalidDateError(error: unknown): error is InvalidDateError {
  return error instanceof InvalidDateError;
}

/**
 * Type guard to check if an error is a FundNotFoundError
 */
export function isFundNotFoundError(error: unknown): error is FundNotFoundError {
  return error instanceof FundNotFoundError;
}

/**
 * Type guard to check if an error is a DateRangeError
 */
export function isDateRangeError(error: unknown): error is DateRangeError {
  return error instanceof DateRangeError;
}

/**
 * Type guard to check if an error is a TefasApiError
 */
export function isTefasApiError(error: unknown): error is TefasApiError {
  return error instanceof TefasApiError;
}

/**
 * Type guard to check if an error is a NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Type guard to check if an error is a ResponseValidationError
 */
export function isResponseValidationError(error: unknown): error is ResponseValidationError {
  return error instanceof ResponseValidationError;
}

/**
 * Type guard to check if an error is a ConfigurationError
 */
export function isConfigurationError(error: unknown): error is ConfigurationError {
  return error instanceof ConfigurationError;
}

/**
 * Type guard to check if an error is an HttpError
 */
export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

/**
 * Type guard to check if an error is a TimeoutError
 */
export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

/**
 * Type guard to check if an error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * HTTP error class for REST API client
 */
export class HttpError extends ApiError {
  public readonly status: number;
  public readonly statusText: string;
  public readonly responseData?: unknown;

  constructor(message: string, status: number, statusText: string, responseData?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
    this.responseData = responseData;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HttpError);
    }
  }
}

/**
 * Timeout error class for REST API client
 */
export class TimeoutError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TimeoutError);
    }
  }
}

/**
 * Validation error class for REST API client
 */
export class ValidationError extends ApiError {
  public readonly validationDetails?: unknown;

  constructor(message: string, validationDetails?: unknown) {
    super(message);
    this.name = 'ValidationError';
    this.validationDetails = validationDetails;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }
}